import React, { useState } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'

/**
 * Bronson Wizard component.
 * @see https://bronson.vwfs.tools/default/components/detail/bronson-wizard.html
 *
 * @param {WizardItem[]} [children] - A collection of {@link WizardItem}.
 * @param {string} [className] - Additional CSS classes on the component element.
 * @param {('s'|'m'|'l')} [collapseBreakpoint] - The breakpoint to collapse the Wizard into vertical mode.
 * @param {string} [collapsedCounter] - The counter label to signalize the current progress, e.g. '6/9'.
 * @param {string} [collapsedText] - Additional text to display in vertical mode.
 * @param {string} [collapsedTitle] - The label shown when in vertical mode.
 * @param {boolean} [defaultOpen=false] - Set the initial open/close state.
 * @param {boolean} [noInteraction] - Disable any pointer interaction with {@link WizardItem}.
 * @param {string} [testId] - The `[data-testid]` attribute on the component element.
 * @param {object} [otherProps] - Other props passed to the component element.
 * @returns {JSX.Element} - The Wizard component.
 * @constructor
 */
export function Wizard({
  children,
  className,
  collapseBreakpoint,
  collapsedCounter,
  collapsedText,
  collapsedTitle,
  defaultOpen = false,
  noInteraction,
  testId,
  ...otherProps
}) {
  const [open, setOpen] = useState(defaultOpen)

  const navClassNameList = classNames(
    'c-wizard',
    'js-wizard',
    'js-is-ready', // Used in CSS.
    {
      'c-wizard--no-interaction ': noInteraction, // Convenience prop from Bronson variants.
    },
    className
  ).trim()

  const olClassNameList = classNames(
    'c-wizard__list',
    'js-wizard__list',
    'js-is-ready',
    {
      'is-closed': !open,
    },
    className
  ).trim()

  return (
    <>
      <nav className={navClassNameList} data-wizard-collapse={collapseBreakpoint} data-testid={testId} {...otherProps}>
        <button
          className="c-wizard__trigger js-wizard__trigger"
          type="button"
          aria-expanded={open}
          title={collapsedTitle}
          onClick={() => setOpen(!open)}
        >
          <i className="c-wizard__trigger-icon" aria-hidden="true" role="img" />
          {collapsedText && <span className="c-wizard__trigger-text">{collapsedText}</span>}
          {collapsedCounter && <span className="c-wizard__trigger-counter">{collapsedCounter}</span>}
        </button>
        <ol className={olClassNameList}>{children}</ol>
      </nav>
    </>
  )
}

Wizard.propTypes = {
  children: PropTypes.node, // Bronson template: 'wizard-item'. Use {@link WizardItem} component.
  className: PropTypes.string, // Bronson template: 'wizard-modifier'.
  collapseBreakpoint: PropTypes.oneOf(['s', 'm', 'l']), // Bronson template: 'collapse-breakpoint'.
  collapsedCounter: PropTypes.string, // Bronson template: 'wizard-trigger-counter'.
  collapsedText: PropTypes.string, // Bronson template: 'wizard-trigger-text'.
  collapsedTitle: PropTypes.string,
  defaultOpen: PropTypes.bool, // Bronson template: 'open'.
  testId: PropTypes.string, // Added for the `[data-testid]` attribute.
  /* Convenience props. */
  noInteraction: PropTypes.bool, // Convenience prop for `.c-wizard--no-interaction`. Bronson template: 'wizard-modifier'.
}

/**
 * Bronson WizardItem component (nested).
 *
 * @param {boolean} [active] - Render the item in an active state.
 * @param {React.ReactNode} [children] - The WizardItem label.
 * @param {string} [className] - Additional CSS classes on the component element.
 * @param {boolean} [disabled] - Render the item in a disabled state.
 * @param {boolean} [done] - Render the item in a done state.
 * @param {boolean} [error] - Render the item in an error state.
 * @param {string} [hiddenCurrentStepText='Current Step'] - Hidden current step label for accessibility.
 * @param {boolean} [noInteraction] - Disable any pointer interaction.
 * @param {function} [onClick] - Callback when the item is clicked.
 * @param {string} [testId] - The `[data-testid]` attribute on the component element.
 * @param {object} [otherProps] - Other props passed to the `<a>` link element.
 * @returns {JSX.Element} - The WizardItem component.
 * @constructor
 */
function WizardItem({
  active,
  children,
  className,
  disabled,
  done,
  error,
  hiddenCurrentStepText = 'Current Step',
  noInteraction,
  onClick,
  testId,
  ...otherProps
}) {
  const liClassNameList = classNames(
    'c-wizard__item',
    {
      'is-done': done,
      'is-active': active,
      'is-error': error,
      'is-disabled': disabled,
      'has-no-interaction': noInteraction,
    },
    className
  ).trim()

  function renderIfStateActive() {
    return active ? <span className="u-visually-hidden">{hiddenCurrentStepText}:</span> : null
  }

  return (
    <li data-testid={testId} className={liClassNameList}>
      {/* The `[href]` will be passed via `otherProps` and makes this an interactive element. */}
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
      <a className="c-wizard__link" onClick={onClick} aria-disabled={disabled} {...otherProps}>
        {renderIfStateActive()}
        <span className="c-wizard__link-title">{children}</span>
      </a>
    </li>
  )
}

WizardItem.propTypes = {
  active: PropTypes.bool, // Bronson template: 'state'.
  children: PropTypes.node, // Bronson template: 'title'.
  className: PropTypes.string, // Additional CSS classes on the component element.
  disabled: PropTypes.bool, // Bronson template: 'state'.
  done: PropTypes.bool, // Bronson template: 'state'.
  error: PropTypes.bool, // Bronson template: 'state'.
  hiddenCurrentStepText: PropTypes.string,
  noInteraction: PropTypes.bool, // Bronson template: 'state'.
  onClick: PropTypes.func, // Added for `[onClick]` attribute.
  testId: PropTypes.string, // Added for the `[data-testid]` attribute.
}

WizardItem.displayName = 'Wizard.Item'
Wizard.Item = WizardItem
